/**
 * Full dance detail page showing complete notation
 */

import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDance } from '../hooks/useDances';
import { useAuth } from '../contexts/AuthContext';
import { danceApi } from '../api/danceApi';

export function DanceDetailPage() {
  const { danceId } = useParams<{ danceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const generationState = location.state as { validationScore?: number; attempt?: number } | null;
  const { user } = useAuth();
  const { data: dance, isLoading, isError, error } = useDance(danceId!);
  const [deleting, setDeleting] = useState(false);

  // Track page view with Datadog RUM
  if (dance && window.DD_RUM) {
    window.DD_RUM.addAction('dance_viewed', {
      danceId: dance.danceId,
      source: dance.source,
      title: dance.title
    });
  }

  if (isLoading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
        <div className="skeleton skeleton-badge" style={{ width: '8rem', marginBottom: 'var(--spacing-md)' }}></div>
        <div className="skeleton-card">
          <div className="skeleton skeleton-title"></div>
          <div className="skeleton skeleton-text" style={{ width: '40%' }}></div>
          <div className="skeleton skeleton-text" style={{ width: '30%', marginBottom: 'var(--spacing-xl)' }}></div>
          <div className="skeleton skeleton-section"></div>
          <div className="skeleton skeleton-section"></div>
          <div className="skeleton skeleton-section"></div>
          <div className="skeleton skeleton-section"></div>
        </div>
      </div>
    );
  }

  if (isError || !dance) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          Error loading dance: {error?.message || 'Dance not found'}
        </div>
        <button
          onClick={() => navigate('/browse')}
          style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
        >
          Back to Browse
        </button>
      </div>
    );
  }

  const handleCopyToClipboard = () => {
    const text = `${dance.title}${dance.choreographer ? ` by ${dance.choreographer}` : ''}
Formation: ${dance.formation}

A1: ${dance.a1}
A2: ${dance.a2}
B1: ${dance.b1}
B2: ${dance.b2}

${dance.notes ? `Notes: ${dance.notes}` : ''}`;

    navigator.clipboard.writeText(text);
    alert('Dance copied to clipboard!');
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this dance? This cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await danceApi.deleteDance(danceId!);
      navigate('/browse');
    } catch (err) {
      alert(`Failed to delete dance: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setDeleting(false);
    }
  };

  // Check if current user owns this dance
  const canEdit = dance && user && dance.userId === user.userId;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <button
        onClick={() => navigate('/browse')}
        className="btn btn-secondary"
        style={{ marginBottom: 'var(--spacing-md)' }}
      >
        ← Back to Browse
      </button>

      <div className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--spacing-lg)' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '2rem' }}>{dance.title}</h1>
            {dance.choreographer && (
              <p style={{ margin: 'var(--spacing-sm) 0', fontSize: '1.1rem', color: 'var(--color-text-secondary)' }}>
                by {dance.choreographer}
              </p>
            )}
          </div>
          <span className={`badge ${dance.source === 'AI' ? 'badge-ai' : 'badge-manual'}`} style={{ fontSize: '0.9rem', padding: 'var(--spacing-sm) var(--spacing-md)' }}>
            {dance.source}
          </span>
        </div>

        <p style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-xl)', fontWeight: 'bold', color: 'var(--color-text)' }}>
          Formation: {dance.formation}
        </p>

        <div className="dance-section dance-section-a1">
          <div className="dance-section-header">A1 (16 beats)</div>
          <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.6' }}>{dance.a1}</p>
        </div>

        <div className="dance-section dance-section-a2">
          <div className="dance-section-header">A2 (16 beats)</div>
          <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.6' }}>{dance.a2}</p>
        </div>

        <div className="dance-section dance-section-b1">
          <div className="dance-section-header">B1 (16 beats)</div>
          <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.6' }}>{dance.b1}</p>
        </div>

        <div className="dance-section dance-section-b2">
          <div className="dance-section-header">B2 (16 beats)</div>
          <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.6' }}>{dance.b2}</p>
        </div>

        {dance.notes && (
          <div style={{ marginTop: 'var(--spacing-xl)', padding: 'var(--spacing-md)', backgroundColor: '#f9f9f9', borderRadius: 'var(--radius-sm)', borderLeft: '4px solid var(--color-secondary)' }}>
            <h4 style={{ marginTop: 0 }}>Notes:</h4>
            <p style={{ marginBottom: 0 }}>{dance.notes}</p>
          </div>
        )}

        {(dance.difficulty || dance.style || dance.energyLevel || dance.programRole) && (
          <div style={{ marginTop: 'var(--spacing-lg)', display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
            {dance.difficulty && (
              <span className="badge badge-difficulty">Difficulty: {dance.difficulty}</span>
            )}
            {dance.style && (
              <span className="badge badge-style">Style: {dance.style}</span>
            )}
            {dance.energyLevel && (
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: '600',
                backgroundColor: dance.energyLevel === 'high' ? '#dc3545' : dance.energyLevel === 'medium' ? '#fd7e14' : '#28a745',
                color: 'white'
              }}>
                Energy: {dance.energyLevel}
              </span>
            )}
            {dance.programRole && (
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: '600',
                backgroundColor: '#6f42c1',
                color: 'white'
              }}>
                Role: {dance.programRole}
              </span>
            )}
          </div>
        )}

        {dance.tags && dance.tags.length > 0 && (
          <div style={{ marginTop: 'var(--spacing-md)' }}>
            <strong style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>Tags:</strong>
            {dance.tags.map(tag => (
              <span key={tag} className="tag-chip">{tag}</span>
            ))}
          </div>
        )}

        {dance.moves && dance.moves.length > 0 && (
          <div style={{ marginTop: 'var(--spacing-md)' }}>
            <strong style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>Moves:</strong>
            {dance.moves.map(move => (
              <span key={move} style={{
                display: 'inline-block',
                margin: '2px',
                padding: '0.2rem 0.6rem',
                backgroundColor: '#d4edda',
                color: '#155724',
                borderRadius: '12px',
                fontSize: '0.875rem',
                border: '1px solid #c3e6cb'
              }}>
                {move}
              </span>
            ))}
          </div>
        )}

        {(dance.music || dance.musicNotes || dance.musicSource) && (
          <div style={{
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#e8f4f8',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid #17a2b8'
          }}>
            <strong style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>Music:</strong>
            {dance.music && <p style={{ margin: '0 0 var(--spacing-xs)' }}>{dance.music}</p>}
            {dance.musicNotes && <p style={{ margin: '0 0 var(--spacing-xs)', fontSize: '0.9rem', color: '#555' }}>{dance.musicNotes}</p>}
            {dance.musicSource && <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Source: {dance.musicSource}</p>}
          </div>
        )}

        {dance.callerNotes && (
          <div style={{
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#fff3cd',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid #ffc107'
          }}>
            <strong style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>Caller Notes:</strong>
            <p style={{ margin: 0 }}>{dance.callerNotes}</p>
          </div>
        )}

        {dance.teachingFigures && dance.teachingFigures.length > 0 && (
          <div style={{ marginTop: 'var(--spacing-md)' }}>
            <strong style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>Walk Through Before Calling:</strong>
            {dance.teachingFigures.map(figure => (
              <span key={figure} style={{
                display: 'inline-block',
                margin: '2px',
                padding: '0.2rem 0.6rem',
                backgroundColor: '#fff3cd',
                color: '#856404',
                borderRadius: '12px',
                fontSize: '0.875rem',
                border: '1px solid #ffc107'
              }}>
                {figure}
              </span>
            ))}
          </div>
        )}

        {dance.source === 'AI' && dance.aiModel && (
          <div style={{ marginTop: 'var(--spacing-xl)', padding: 'var(--spacing-md)', backgroundColor: 'var(--color-ai-bg)', borderRadius: 'var(--radius-sm)', fontSize: '0.9rem', borderLeft: '4px solid var(--color-ai-text)' }}>
            <p style={{ margin: 'var(--spacing-xs) 0' }}>
              <strong>Generated by:</strong> {dance.aiModel}
            </p>
            {dance.aiPrompt && (
              <p style={{ margin: 'var(--spacing-xs) 0' }}>
                <strong>Prompt:</strong> {dance.aiPrompt}
              </p>
            )}
            {dance.generationCost && (
              <p style={{ margin: 'var(--spacing-xs) 0' }}>
                <strong>Tokens used:</strong> {dance.generationCost}
              </p>
            )}
            {generationState?.validationScore != null && (
              <div style={{
                marginTop: 'var(--spacing-sm)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}>
                <strong>Quality:</strong>
                <span style={{
                  padding: '0.2rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  backgroundColor: generationState.validationScore >= 90 ? '#28a745'
                    : generationState.validationScore >= 70 ? '#fd7e14' : '#dc3545',
                  color: 'white'
                }}>
                  {generationState.validationScore >= 90 ? 'Excellent'
                    : generationState.validationScore >= 70 ? 'Good' : 'Needs Review'}
                  {' '}({generationState.validationScore}/100)
                </span>
                {generationState.attempt && generationState.attempt > 1 && (
                  <span style={{ fontSize: '0.85rem', color: '#666' }}>
                    (refined in {generationState.attempt} attempts)
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 'var(--spacing-xl)', display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
          <button
            onClick={handleCopyToClipboard}
            className="btn"
            style={{ backgroundColor: 'var(--color-success)', color: 'white' }}
          >
            Copy to Clipboard
          </button>

          <button
            onClick={() => window.print()}
            className="btn btn-primary"
          >
            Print
          </button>

          {dance.source === 'AI' && dance.aiPrompt && (
            <button
              onClick={() => navigate(`/generate?prompt=${encodeURIComponent(dance.aiPrompt || '')}&difficulty=${dance.difficulty || ''}&style=${dance.style || ''}&energyLevel=${dance.energyLevel || ''}&programRole=${dance.programRole || ''}`)}
              className="btn"
              style={{ backgroundColor: '#17a2b8', color: 'white' }}
            >
              Regenerate
            </button>
          )}

          {canEdit && (
            <>
              <button
                onClick={() => navigate(`/dances/${danceId}/edit`)}
                className="btn btn-warning"
              >
                Edit
              </button>

              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn btn-danger"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
